import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Direct-upload for the advocate application photos. On selection it validates
// (count/type/size), uploads each image to the same-origin endpoint, drops the
// returned signed_id into a hidden field, and renders a thumbnail tile — so a
// validation re-render restores the photos instead of dropping them (a file
// input can't be repopulated by the server). The raw <input> is the non-JS
// fallback; once a file is uploaded we clear the input so its bytes aren't
// submitted a second time alongside the signed_id.
//
//   <div data-controller="photo-upload"
//        data-photo-upload-url-value="/join/photos"
//        data-photo-upload-param-value="media_signed_ids"
//        data-photo-upload-multiple-value="true"
//        data-photo-upload-max-files-value="10"
//        data-photo-upload-max-bytes-value="10485760"
//        data-photo-upload-accept-value="image/jpeg,image/png,image/webp,image/heic">
//     <input type="file" multiple data-photo-upload-target="input"
//            data-action="change->photo-upload#changed">
//     <div data-photo-upload-target="tiles"></div>
//     <div data-photo-upload-target="error"></div>
//   </div>
export default class extends Controller {
  static targets = ["input", "tiles", "error"]

  static values = {
    url: String,
    param: String,
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 10 },
    maxBytes: { type: Number, default: 10 * 1024 * 1024 },
    accept: {
      type: String,
      default: "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif",
    },
  }

  async changed() {
    const files = Array.from(this.inputTarget.files)
    // Never submit the raw bytes — the signed_ids carry the selection.
    this.inputTarget.value = ""
    if (!files.length) return

    this.clearError()
    const accepted = this.acceptValue.split(",")
    const maxMb = Math.round(this.maxBytesValue / 1024 / 1024)
    const errors = []

    for (const file of files) {
      if (!this.multipleValue) this.clearTiles()

      if (this.tileCount >= this.maxFilesValue) {
        const noun = this.maxFilesValue === 1 ? "photo" : "photos"
        errors.push(`Up to ${this.maxFilesValue} ${noun}.`)
        break
      }
      // Browsers report an empty type for formats they can't decode (HEIC in
      // everything but Safari), so only reject a type the browser actually
      // named — the server sniffs the bytes and is the authority either way.
      if (file.type && !accepted.includes(file.type)) {
        errors.push(`${file.name} isn't a JPG, PNG, WebP, GIF, or HEIC.`)
        continue
      }
      if (file.size > this.maxBytesValue) {
        errors.push(`${file.name} is larger than ${maxMb} MB.`)
        continue
      }

      await this.upload(file, errors)
    }

    if (errors.length) this.showError([...new Set(errors)].join(" "))
  }

  async upload(file, errors) {
    const objectUrl = URL.createObjectURL(file)
    const tile = this.buildTile(objectUrl, file.name)
    tile.dataset.uploading = "true"
    this.tilesTarget.append(tile)

    try {
      const body = new FormData()
      body.append("file", file)

      const response = await post(this.urlValue, { body, responseKind: "json" })
      const data = await this.errorSafeJson(response)

      if (response.ok && data?.signed_id) {
        this.appendSignedId(tile, data.signed_id)
        // Swap the local preview for the server's WebP rendering. The browser
        // can't necessarily decode what was selected (HEIC outside Safari), but
        // it can always decode the variant we get back.
        if (data.thumb_url) this.replacePreview(tile, data.thumb_url)
        delete tile.dataset.uploading
        return
      }

      tile.remove()
      // No `error` in the body means something upstream answered instead of the
      // app (proxy error page, gateway timeout) — name the status, since a bare
      // "couldn't upload" gives the advocate and us nothing to act on.
      errors.push(
        data?.error ||
          `Couldn't upload ${file.name} (error ${response.statusCode}). Please try again.`
      )
    } catch {
      // post() rejects only when the request itself failed: offline, dropped
      // connection, aborted. Retrying is the right advice; the server is fine.
      tile.remove()
      errors.push(
        `Couldn't upload ${file.name} — check your connection and try again.`
      )
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }

  // Only drop the local preview once the variant has actually decoded, so a
  // failed swap leaves the tile as it was rather than blanking it.
  replacePreview(tile, url) {
    const img = tile.querySelector("img")
    if (!img) return

    const rendered = new Image()
    rendered.addEventListener("load", () => {
      img.src = url
    })
    rendered.src = url
  }

  // `response.json` rejects when the body isn't JSON, which would otherwise be
  // indistinguishable from a network failure. Swallow it so the caller can fall
  // back to the status code.
  async errorSafeJson(response) {
    try {
      return await response.json
    } catch {
      return null
    }
  }

  remove(event) {
    event.target.closest("[data-photo-upload-tile]")?.remove()
    this.clearError()
  }

  // --- tile construction (mirrors the server-rendered tile DOM) ---

  buildTile(src, alt) {
    const tile = document.createElement("div")
    tile.setAttribute("data-photo-upload-tile", "")
    tile.className =
      "relative aspect-square overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-700 data-[uploading]:opacity-50"

    const img = document.createElement("img")
    img.src = src
    img.alt = alt
    img.className = "h-full w-full object-cover"

    const remove = document.createElement("button")
    remove.type = "button"
    remove.setAttribute("aria-label", "Remove photo")
    remove.setAttribute("data-action", "photo-upload#remove")
    remove.className =
      "absolute right-1 top-1 inline-flex size-5 items-center justify-center rounded-full bg-black/60 text-xs text-white"
    remove.textContent = "×"

    tile.append(img, remove)
    return tile
  }

  appendSignedId(tile, signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.multipleValue
      ? `form[${this.paramValue}][]`
      : `form[${this.paramValue}]`
    input.value = signedId
    tile.append(input)
  }

  clearTiles() {
    this.tiles.forEach((tile) => tile.remove())
  }

  get tiles() {
    return Array.from(
      this.tilesTarget.querySelectorAll("[data-photo-upload-tile]")
    )
  }

  get tileCount() {
    return this.tiles.length
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
